// src/features/feedback/services/feedbackService.js
import { apiClient } from '../../../services/api/client';

export class FeedbackService {
  /**
   * Submit new feedback
   */
  async submitFeedback(data) {
    try {
      const response = await apiClient.post('/api/v1/feedback/feedback/', data);
      return response.data;
    } catch (error) {
      throw this.handleFeedbackError(error);
    }
  }

  /**
   * Get current user's feedback
   */
  async getMyFeedback({ limit = 20, offset = 0 } = {}) {
    try {
      const response = await apiClient.get('/api/v1/feedback/feedback/my', {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      throw this.handleFeedbackError(error);
    }
  }

  /**
   * Get all published feedback (visible to everyone)
   */
  async getPublishedFeedback({ limit = 20, offset = 0 } = {}) {
    try {
      const response = await apiClient.get('/api/v1/feedback/feedback/published', {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      throw this.handleFeedbackError(error);
    }
  }

  /**
   * Get department feedback (visible to department)
   */
  async getDepartmentFeedback({ limit = 20, offset = 0 } = {}) {
    try {
      const response = await apiClient.get('/api/v1/feedback/feedback/department', {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      throw this.handleFeedbackError(error);
    }
  }

  /**
   * Get specific feedback details
   */
  async getFeedbackDetails(feedbackId) {
    try {
      const response = await apiClient.get(`/api/v1/feedback/feedback/${feedbackId}`);
      return response.data;
    } catch (error) {
      throw this.handleFeedbackError(error);
    }
  }

  /**
   * Get feedback statistics (admin only)
   */
  async getFeedbackStatistics() {
    try {
      const response = await apiClient.get('/api/v1/feedback/feedback/statistics/overview');
      return response.data;
    } catch (error) {
      throw this.handleFeedbackError(error);
    }
  }

  /**
   * Update feedback visibility (admin only)
   */
  async updateFeedbackVisibility(feedbackId, visibility) {
    try {
      const response = await apiClient.patch(
        `/api/v1/feedback/feedback/${feedbackId}/visibility`,
        { visibility }
      );
      return response.data;
    } catch (error) {
      throw this.handleFeedbackError(error);
    }
  }

  /**
   * Update feedback status (admin only)
   */
  async updateFeedbackStatus(feedbackId, data) {
    try {
      const response = await apiClient.patch(
        `/api/v1/feedback/feedback/${feedbackId}/status`,
        data
      );
      return response.data;
    } catch (error) {
      throw this.handleFeedbackError(error);
    }
  }

  /**
   * Delete feedback (admin only)
   */
  async deleteFeedback(feedbackId) {
    try {
      const response = await apiClient.delete(`/api/v1/feedback/feedback/${feedbackId}`);
      return response.data;
    } catch (error) {
      throw this.handleFeedbackError(error);
    }
  }

  /**
 * Filter feedback with advanced criteria
 */
  async filterFeedback(filters, { limit = 20, offset = 0 } = {}) {
    try {
      const response = await apiClient.post(
        '/api/v1/feedback/feedback/filter',
        filters,
        { params: { limit, offset } }
      );
      return response.data;
    } catch (error) {
      throw this.handleFeedbackError(error);
    }
  }

  /**
   * Handle feedback-specific errors
   */
  handleFeedbackError(error) {
    const errorData = error.response?.data;

    if (errorData?.error_code) {
      switch (errorData.error_code) {
        case 'FEEDBACK__NOT_FOUND':
          return new Error('Feedback not found');
        case 'DEPARTMENT__NOT_FOUND':
          return new Error('Department not found');
        default:
          return new Error(errorData.message || 'Feedback operation failed');
      }
    }

    return new Error(error.message || 'Network error occurred');
  }
}

export const feedbackService = new FeedbackService();